import { useEffect, useState } from "react";

/**
 * Lightweight device-capability probe.
 *
 * - `isLowEndDevice`: hardwareConcurrency ≤ 4 OR deviceMemory ≤ 4 (heuristic,
 *   both properties are advisory). Used to skip scroll-triggered animations
 *   on low-power mobile hardware where they visibly stutter.
 * - `canHover`: mirrors `(hover: hover)`. On touch devices we skip
 *   `whileHover` because it fires after the tap and feels delayed — we keep
 *   `whileTap` for immediate feedback.
 *
 * Values are computed once at mount (device class doesn't change during a
 * session) and updated when the hover media query flips (external mouse
 * attached to iPad, etc.).
 */
export function useDeviceCapabilities() {
  const [state, setState] = useState(() => detect());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(hover: hover)");
    const onChange = () => setState(detect());
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);

  return state;
}

function detect() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { isLowEndDevice: false, canHover: true };
  }
  const cores = navigator.hardwareConcurrency ?? 8;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  const isLowEndDevice = cores <= 4 || memory <= 4;
  const canHover = window.matchMedia("(hover: hover)").matches;
  return { isLowEndDevice, canHover };
}
